import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { connect } from "react-redux";
import { post, put } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import CustomInput from "../../../components/AuthenticationComponents/CustomInput";
import SecondaryButton from "../../../components/Common/SecondaryButton";
import { ToggleRow } from "../../../components/Common/PageWidgets";
import tickIcon from "../../../assets/images/support/tick-circle-white.svg";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { extractErrorMessage } from "../../../utils/errorHandler";
import cancelIcon from "../../../assets/images/user-dashboard/remove-circle.svg";

/* -------------------------------------------------------------------------
 * AdminFAQCreatePage — create/edit form for a single FAQ.
 *
 * `faqId` is a URL route param (":faqId/edit"), not a prop — it must
 * be read with useParams(). 
 * 
 * Edit mode uses the FAQ data passed via navigation state from the
 * list page to avoid unnecessary API calls.
 * ---------------------------------------------------------------------- */

const AdminFAQCreatePage = ({ mode = "create", user }) => {
  document.title = mode === "create" ? "Create FAQ | Omaha Food Lovers" : "Edit FAQ | Omaha Food Lovers";
  const navigate = useNavigate();
  const location = useLocation();
  const { faqId } = useParams();
  const [loading, setLoading] = useState(false);

  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  // Populate form from navigation state for edit mode
  useEffect(() => {
    if (mode === "edit" && faqId) {
      const stateFAQData = location.state?.faqData;
      if (stateFAQData) {
        // Use the data passed from the list page
        formik.setValues({
          question: stateFAQData.question || "",
          answer: stateFAQData.answer || "",
          category: stateFAQData.category || "",
          sort_order: stateFAQData.sortOrder || 1,
          is_active: stateFAQData.isActive === "Active" ? true : false,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, faqId]);

  const validationSchema = Yup.object().shape({
    question: Yup.string()
      .required("Question is required")
      .min(5, "Question must be at least 5 characters")
      .max(500, "Question must not exceed 500 characters"),
    answer: Yup.string()
      .required("Answer is required")
      .min(10, "Answer must be at least 10 characters")
      .max(2000, "Answer must not exceed 2000 characters"),
    category: Yup.string()
      .required("Category is required")
      .max(100, "Category must not exceed 100 characters"),
    sort_order: Yup.number()
      .required("Sort order is required")
      .min(0, "Sort order must be 0 or greater")
      .max(9999, "Sort order must not exceed 9999"),
    is_active: Yup.boolean(),
  });

  const formik = useFormik({
    initialValues: {
      question: "",
      answer: "",
      category: "",
      sort_order: 1,
      is_active: true,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setTouched, validateForm }) => {
      setTouched({
        question: true,
        answer: true,
        category: true,
        sort_order: true,
        is_active: true,
      });

      const errors = await validateForm();
      if (Object.keys(errors).length > 0) return;

      setLoading(true);

      try {
        const payload = {
          question: values.question,
          answer: values.answer,
          category: values.category,
          sort_order: values.sort_order,
          is_active: values.is_active,
        };

        let response;
        if (mode === "create") {
          response = await post(endpoints.adminGlobalFAQs, payload);
        } else if (mode === "edit" && faqId) {
          const endpoint = endpoints.adminGlobalFAQ.replace("{{global_faq_id}}", faqId);
          response = await put(endpoint, payload);
        }

        if (response?.data?.success) {
          toastr.success(
            mode === "create" ? "FAQ created successfully" : "FAQ updated successfully"
          );
          navigate("/admin/faqs");
        }
      } catch (err) {
        console.error("Failed to save FAQ:", err);
        if (err.response?.data?.errors) {
          const validationErrors = Object.values(err.response.data.errors)
            .filter((value) => Array.isArray(value))
            .flat();
          toastr.error(validationErrors.length > 0 ? validationErrors.join(", ") : extractErrorMessage(err));
        } else {
          toastr.error(extractErrorMessage(err));
        }
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <div className="page-content rp-page-content">
      <Container fluid>
        <div className="rp-page">
          <div className="rp-page-header">
            <h2 className="rp-page-header__title">
              {mode === "create" ? "Create FAQ" : "Edit FAQ"}
            </h2>
            <p className="rp-page-header__subtitle">
              {mode === "create" ? "Add a new FAQ to the platform" : "Update FAQ information"}
            </p>
          </div>

          <Form onSubmit={formik.handleSubmit}>
            <Row>
              <Col sm={12}>
                <CustomInput
                  id="question"
                  label="Question"
                  name="question"
                  type="textarea"
                  placeholder="Enter the question"
                  value={formik.values.question}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.question && !!formik.errors.question}
                  errorText={formik.errors.question}
                  className="white-bg"
                  rows={3}
                />
              </Col>
            </Row>

            <Row>
              <Col sm={12}>
                <CustomInput
                  id="answer"
                  label="Answer"
                  name="answer"
                  type="textarea"
                  placeholder="Enter the answer"
                  value={formik.values.answer}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.answer && !!formik.errors.answer}
                  errorText={formik.errors.answer}
                  className="white-bg"
                  rows={5}
                />
              </Col>
            </Row>

            <Row>
              <Col xl={6} lg={6} md={12} sm={12}>
                <CustomInput
                  id="category"
                  label="Category"
                  name="category"
                  type="text"
                  placeholder="Enter category (e.g., Rewards, Account, etc.)"
                  value={formik.values.category}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.category && !!formik.errors.category}
                  errorText={formik.errors.category}
                  className="white-bg"
                />
              </Col>
              <Col xl={6} lg={6} md={12} sm={12}>
                <CustomInput
                  id="sort_order"
                  label="Sort Order"
                  name="sort_order"
                  type="number"
                  placeholder="Enter sort order"
                  value={formik.values.sort_order}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.sort_order && !!formik.errors.sort_order}
                  errorText={formik.errors.sort_order}
                  className="white-bg"
                  min="0"
                  max="9999"
                />
              </Col>
            </Row>

            <Row>
              <Col sm={12}>
                <ToggleRow
                  label="Active"
                  checked={formik.values.is_active}
                  onChange={(checked) => formik.setFieldValue('is_active', checked)}
                  style={{ padding: '19px 12px', minHeight: '48px', borderRadius: '16px', border: '1px solid var(--border-grey)' }}
                />
              </Col>
            </Row>

              <div className="mt-4">
                <SecondaryButton
                  type="submit"
                  variant="orange"
                  disabled={loading}
                  loading={loading}
                  icon={tickIcon}
                  iconPosition="right"
                >
                  {loading ? "Saving..." : mode === "create" ? "Create FAQ" : "Update FAQ"}
                </SecondaryButton>
                <SecondaryButton
                  type="button"
                  variant="light-grey"
                  onClick={() => navigate("/admin/faqs")}
                  icon={cancelIcon}
                  iconPosition="right"
                  style={{ marginLeft: "10px" }}
                >
                  Cancel
                </SecondaryButton>
              </div>
            </Form>
        </div>
      </Container>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.Login.user,
});

export default connect(mapStateToProps, null)(AdminFAQCreatePage);
